/**
 * Admin — Audit trail viewer.
 * Filters: action, actor id/email, patient id, outcome, and a text-search on any
 * of the above. Reads /api/audit/events (Phase B) which itself enforces the
 * per-role visibility rules (patients=403; clinicians restricted to their
 * caseload; platform admins see events with change-deltas stripped).
 */
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Loader2, RefreshCcw, Filter } from "lucide-react";
import AdminLayout from "@/components/AdminLayout";
import api from "@/lib/api";
import { Skeleton } from "@/components/Skeleton";

const OUTCOME_CLS = {
  success: "text-emerald-400",
  failure: "text-red-400",
  denied: "text-amber-400",
};

export default function AdminAudit() {
  const [events, setEvents] = useState([]);
  const [actions, setActions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ action: "", actor_id: "", patient_id: "", outcome: "", search: "" });

  const load = async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams();
      if (filters.action) qs.set("action", filters.action);
      if (filters.actor_id) qs.set("actor_id", filters.actor_id.trim());
      if (filters.patient_id) qs.set("patient_id", filters.patient_id.trim());
      if (filters.outcome) qs.set("outcome", filters.outcome);
      qs.set("limit", "300");
      const { data } = await api.get(`/audit/events?${qs.toString()}`);
      setEvents(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Load failed"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [filters.action, filters.actor_id, filters.patient_id, filters.outcome]);
  useEffect(() => { api.get("/audit/actions").then((r) => setActions(r.data)).catch(() => {}); }, []);

  const filtered = useMemo(() => {
    const s = filters.search.trim().toLowerCase();
    if (!s) return events;
    return events.filter((e) =>
      (e.action || "").toLowerCase().includes(s) ||
      (e.actor_email || "").toLowerCase().includes(s) ||
      (e.actor_id || "").toLowerCase().includes(s) ||
      (e.patient_id || "").toLowerCase().includes(s) ||
      (e.target_id || "").toLowerCase().includes(s) ||
      (e.ip || "").toLowerCase().includes(s)
    );
  }, [events, filters.search]);

  const clearFilters = () => setFilters({ action: "", actor_id: "", patient_id: "", outcome: "", search: "" });

  return (
    <AdminLayout>
      <div className="max-w-6xl">
        <div className="fl-label text-[#f47a20]">Audit</div>
        <h1 className="fl-heading text-3xl md:text-4xl mt-2">Audit trail</h1>
        <p className="text-sm text-zinc-400 mt-2 max-w-2xl">
          Every security-relevant event across authentication, records, exports and role changes. Append-only — never editable through this interface.
        </p>

        {/* Filters */}
        <div className="fl-card fl-card-elevated p-4 mt-6 grid md:grid-cols-6 gap-2" data-testid="audit-filters">
          <select className="fl-input md:col-span-2" value={filters.action} onChange={(e) => setFilters({ ...filters, action: e.target.value })} data-testid="audit-filter-action">
            <option value="">All actions</option>
            {actions.map((a) => <option key={a} value={a}>{a}</option>)}
          </select>
          <select className="fl-input" value={filters.outcome} onChange={(e) => setFilters({ ...filters, outcome: e.target.value })} data-testid="audit-filter-outcome">
            <option value="">Any outcome</option>
            <option value="success">Success</option>
            <option value="failure">Failure</option>
            <option value="denied">Denied</option>
          </select>
          <input className="fl-input" placeholder="Actor id" value={filters.actor_id} onChange={(e) => setFilters({ ...filters, actor_id: e.target.value })} data-testid="audit-filter-actor"/>
          <input className="fl-input" placeholder="Patient id" value={filters.patient_id} onChange={(e) => setFilters({ ...filters, patient_id: e.target.value })} data-testid="audit-filter-patient"/>
          <input className="fl-input" placeholder="Search (email, ip, target)" value={filters.search} onChange={(e) => setFilters({ ...filters, search: e.target.value })} data-testid="audit-filter-search"/>
        </div>
        <div className="mt-3 flex items-center gap-3">
          <button onClick={load} className="fl-btn-ghost text-xs" data-testid="audit-refresh">
            {loading ? <Loader2 size={12} className="animate-spin"/> : <RefreshCcw size={12}/>} Refresh
          </button>
          <button onClick={clearFilters} className="fl-btn-ghost text-xs" data-testid="audit-clear">Clear filters</button>
          <div className="ml-auto text-xs text-zinc-500" data-testid="audit-count">{filtered.length} of {events.length} shown</div>
        </div>

        {/* Table */}
        <div className="fl-card fl-card-elevated overflow-hidden mt-4" data-testid="audit-table">
          <div className="grid grid-cols-12 gap-4 p-3 border-b fl-label" style={{ borderColor: "var(--fl-border)" }}>
            <div className="col-span-2">Time</div>
            <div className="col-span-3">Actor</div>
            <div className="col-span-3">Action</div>
            <div className="col-span-2">Target</div>
            <div className="col-span-2">IP</div>
          </div>
          {loading && (
            <div data-testid="audit-loading">
              {Array.from({ length: 8 }).map((_, i) => (
                <div key={i} className="grid grid-cols-12 gap-4 p-3 border-b" style={{ borderColor: "var(--fl-border)" }}>
                  <div className="col-span-2"><Skeleton className="h-3 w-24 rounded" /></div>
                  <div className="col-span-3">
                    <Skeleton className="h-3 w-40 rounded" />
                    <Skeleton className="h-2 w-16 mt-2 rounded" />
                  </div>
                  <div className="col-span-3">
                    <Skeleton className="h-3 w-28 rounded" />
                  </div>
                  <div className="col-span-2"><Skeleton className="h-3 w-16 rounded" /></div>
                  <div className="col-span-2"><Skeleton className="h-3 w-20 rounded" /></div>
                </div>
              ))}
            </div>
          )}
          {!loading && filtered.length === 0 && (
            <div className="p-8 text-center text-sm text-zinc-500">No events match these filters.</div>
          )}
          {filtered.map((e) => (
            <div key={e.id} className="grid grid-cols-12 gap-4 p-3 border-b text-sm hover:bg-white/5 transition-colors" style={{ borderColor: "var(--fl-border)" }} data-testid={`audit-row-${e.id}`}>
              <div className="col-span-2 text-xs text-zinc-500">{new Date(e.timestamp).toLocaleString("en-GB")}</div>
              <div className="col-span-3">
                <div className="text-zinc-200">{e.actor_email || <span className="italic text-zinc-500">anonymous</span>}</div>
                <div className="text-[10px] text-zinc-500 uppercase tracking-widest">{e.actor_role || "-"}</div>
              </div>
              <div className="col-span-3">
                <span className="text-[#f47a20] text-xs">{e.action}</span>
                <span className={`ml-2 text-[10px] uppercase tracking-widest ${OUTCOME_CLS[e.outcome] || "text-zinc-500"}`}>{e.outcome}</span>
              </div>
              <div className="col-span-2 text-xs text-zinc-400 truncate" title={e.target_id || e.patient_id || ""}>
                {e.target_type ? <span className="text-zinc-500">{e.target_type}: </span> : null}
                <code className="text-[11px]">{(e.target_id || e.patient_id || "—").slice(0, 8)}</code>
              </div>
              <div className="col-span-2 text-xs text-zinc-500">{e.ip || "—"}</div>
            </div>
          ))}
        </div>
      </div>
    </AdminLayout>
  );
}
